import apiClient from "./api";

/**
 * Tenant-facing AI Gateway API.
 *
 * Two shapes of enum cross this boundary and they are not the same:
 *  - **Responses** carry enums as their **names** (`"Aggregator"`, `"Simple"`) because the DTOs
 *    stringify them server-side.
 *  - **Requests** carry enums as their **integer** values. No `JsonStringEnumConverter` is
 *    registered in the API host, so `{ kind: "Aggregator" }` is a bind failure while
 *    `{ kind: 1 }` binds. The `*Value` maps below exist for exactly that reason — do not send
 *    a name.
 *
 * The scope is never a parameter: every endpoint reads the company from the authenticated
 * principal. There is no `companyId` on any request here, and adding one would not do anything.
 */

/** What a connector points at. Response side — see `connectorKindValue` for the request side. */
export type AiConnectorKindName =
  | "SingleProvider"
  | "Aggregator"
  | "CustomEndpoint";

/** The wire format an endpoint speaks, independent of who bills for the call. */
export type ApiDialectName =
  | "OpenAiResponses"
  | "OpenAiChatCompletions"
  | "AnthropicMessages"
  | "GeminiGenerateContent"
  | "BedrockConverse";

/** How the credential is attached to the request. */
export type ConnectorAuthSchemeName =
  | "BearerToken"
  | "ApiKeyHeader"
  | "AwsSigV4"
  | "None";

/** Whether the connector's model list is hand-entered or pulled from the upstream. */
export type CatalogSyncModeName = "Curated" | "Discover";

/** Difficulty tier. The UI labels these Basic / Medium / Difficult; the API does not. */
export type DifficultyTierName = "Simple" | "Moderate" | "Complex";

/**
 * How a call's tier is decided. `DynamicQuestion` classifies each turn before answering it, which
 * costs one extra cheap LLM call — see `AiDifficultyProfile.dynamicRoutingAvailable` for whether
 * the deployment has it switched on at all.
 */
export type RoutingModeName = "StaticStage" | "DynamicQuestion";

/**
 * Circuit-breaker state for one connector. `HalfOpen` is never stored — it is what an elapsed
 * cooldown means, and it says exactly one probe call will be allowed through next.
 */
export type AiCircuitStateName = "Closed" | "Open" | "HalfOpen";

/** Which side of the platform/tenant split a route sits on. */
export type AiPlaneName = "Tenant" | "Platform";

export const connectorKindValue: Record<AiConnectorKindName, number> = {
  SingleProvider: 0,
  Aggregator: 1,
  CustomEndpoint: 2,
};

export const apiDialectValue: Record<ApiDialectName, number> = {
  OpenAiResponses: 0,
  OpenAiChatCompletions: 1,
  AnthropicMessages: 2,
  GeminiGenerateContent: 3,
  BedrockConverse: 4,
};

export const authSchemeValue: Record<ConnectorAuthSchemeName, number> = {
  BearerToken: 0,
  ApiKeyHeader: 1,
  AwsSigV4: 2,
  None: 3,
};

export const catalogSyncModeValue: Record<CatalogSyncModeName, number> = {
  Curated: 0,
  Discover: 1,
};

export const difficultyTierValue: Record<DifficultyTierName, number> = {
  Simple: 0,
  Moderate: 1,
  Complex: 2,
};

export const routingModeValue: Record<RoutingModeName, number> = {
  StaticStage: 0,
  DynamicQuestion: 1,
};

/** Ordered for pickers — the tier ladder is meaningful, alphabetical order is not. */
export const difficultyTiers: DifficultyTierName[] = [
  "Simple",
  "Moderate",
  "Complex",
];

export const connectorKinds: AiConnectorKindName[] = [
  "SingleProvider",
  "Aggregator",
  "CustomEndpoint",
];

/**
 * Dialects a workspace may pick for its own connector. Bedrock (and its AwsSigV4 scheme) is left
 * out on purpose: it runs on StrikeUp's AWS account, so the API refuses it for workspace connectors
 * (`bedrockNotAvailableToTenants`). StrikeUp's own Bedrock connector is managed in the super-admin app.
 */
export const apiDialects: ApiDialectName[] = [
  "OpenAiResponses",
  "OpenAiChatCompletions",
  "AnthropicMessages",
  "GeminiGenerateContent",
];

export const authSchemes: ConnectorAuthSchemeName[] = [
  "BearerToken",
  "ApiKeyHeader",
  "None",
];

/**
 * Error codes the gateway returns in the `code` field of a 400 body. Anything else is prose and
 * the interceptor's generic toast is the right treatment.
 */
export type AiGatewayErrorCode =
  | "connectorUrlNotPublic"
  | "bedrockNotAvailableToTenants"
  | "platformRouteReadOnly"
  | "connectorTestFailed"
  | "connectorHasNoModel"
  | "connectorDialectUnsupported"
  | "budgetNameRequired"
  | "budgetAmountInvalid"
  | "budgetThresholdsInvalid"
  | "budgetWidgetNotFound"
  | "budgetNotifyEmailsInvalid"
  | "budgetScopeInvalid"
  | "budgetCustomerCompanyNotFound";

export interface AiGatewayErrorBody {
  code: AiGatewayErrorCode | null;
  message: string;
}

/**
 * Pulls the typed `code` off a rejected request so a form can render the error against the field
 * that caused it. Returns `null` when the failure was not one of the gateway's own — a network
 * error, a 500, an auth redirect — which the caller should let the generic toast handle.
 */
export const readGatewayErrorCode = (error: any): AiGatewayErrorCode | null =>
  (error?.response?.data as AiGatewayErrorBody | undefined)?.code ?? null;

export interface AiConnector {
  id: string;
  /** `null` for a platform connector — visible to this company, editable by nobody here. */
  companyId: string | null;
  provider: string;
  name: string;
  kind: AiConnectorKindName;
  apiDialect: ApiDialectName;
  authScheme: ConnectorAuthSchemeName;
  region: string | null;
  catalogSyncMode: CatalogSyncModeName;
  defaultModelName: string | null;
  timeoutSeconds: number | null;
  /**
   * Whether this connector skips the public-address egress check. Only a platform administrator
   * can set it, and it is surfaced here precisely so it cannot be set quietly.
   */
  bypassEgressPolicy: boolean;
  baseUrl: string | null;
  isDefault: boolean;
  description: string | null;
  isActive: boolean;
  /** Whether a key is stored. The key itself is never returned by any endpoint. */
  hasApiKey: boolean;
  isApiKeyEncrypted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AiModel {
  id: string;
  companyId: string | null;
  provider: string;
  modelKey: string;
  displayName: string;
  description: string | null;
  supportsTools: boolean;
  supportsStructuredOutput: boolean;
  supportsStreaming: boolean;
  supportsReasoningEffort: boolean;
  supportsTemperature: boolean;
  supportsVision: boolean;
  contextWindowTokens: number | null;
  maxOutputTokens: number | null;
  sortOrder: number;
  isActive: boolean;
  /** Connector this model is reachable through, or `null` for a globally-matched model. */
  aiConnectorId: string | null;
  /** Name of that connector — the same model via two connectors is two different choices. */
  connectorName: string | null;
  upstreamModelId: string | null;
  isDeprecated: boolean;
}

/**
 * Circuit-breaker state for one connector.
 *
 * Only connectors that have failed at least once have a row, so **absent means healthy** and an
 * empty list is the normal answer — a row missing from this list is not an error. The same empty
 * list comes back when resilience is switched off for the deployment, because then nothing is
 * being skipped and a surviving row would be history rather than state.
 */
export interface AiConnectorHealth {
  aiConnectorId: string;
  state: AiCircuitStateName;
  consecutiveFailures: number;
  openedAt: string | null;
  /** End of the current cooldown. While it is in the future the connector is skipped uncalled. */
  openUntil: string | null;
  lastFailureAt: string | null;
  lastFailureReason: string | null;
  lastFailureStatusCode: number | null;
  /** When it last recovered — this is not a heartbeat, a healthy connector does not update it. */
  lastSuccessAt: string | null;
}

export interface AiModelAvailability {
  model: AiModel;
  isAllowed: boolean;
  deniedReason: string | null;
}

export interface AiRouteResolution {
  agentRoute: string | null;
  plane: AiPlaneName;
  provider: string;
  model: string;
  baseUrl: string | null;
  connectorId: string | null;
  connectorName: string | null;
  /** `route-binding` | `agent-config` | `call-override` | `platform-default`. */
  resolvedBy: string;
  explanation: string;
}

export interface AiEffectiveConfig {
  companyId: string | null;
  routes: AiRouteResolution[];
  connectors: AiConnector[];
  models: AiModelAvailability[];
}

/**
 * One attempt after the primary fails. The primary itself is not a link, so an empty chain means
 * "one attempt, then the turn fails" rather than "no configuration".
 */
export interface AiGatewayFallbackLink {
  /** Attempt order. Links arrive already sorted and capped at the depth the executor will run. */
  order: number;
  aiConnectorId: string | null;
  connectorName: string | null;
  aiModelId: string | null;
  /** `null` when the model row is inactive — the link is configured but will not be attempted. */
  modelKey: string | null;
  /** A platform link. A company's own chain replaces the platform's rather than extending it. */
  isPlatformManaged: boolean;
  description: string | null;
}

export interface AiGatewayRoute {
  agentRoute: string;
  plane: AiPlaneName;
  /** Platform-plane routes render read-only; `PUT` on one returns `platformRouteReadOnly`. */
  isReadOnly: boolean;
  tier: DifficultyTierName;
  aiConnectorId: string | null;
  connectorName: string | null;
  aiModelId: string | null;
  modelKey: string | null;
  isActive: boolean;
  /** Read-only here — chains are configured through the internal-admin API, not this page. */
  fallbackChain: AiGatewayFallbackLink[];
  /**
   * For a `subagent_*` route, the route it borrows its binding from while it has none of its own.
   * `null` on every other route. Server-derived — never re-derive it from the `subagent_` prefix
   * here, or `subagent_decomposer` (a platform route) would be mislabelled as inheriting.
   */
  inheritsFrom: string | null;
}

export interface AiDifficultyRoute {
  agentRoute: string;
  plane: AiPlaneName;
  tier: DifficultyTierName;
  /**
   * What the route falls back to unsaved. For a sub-agent route this is the parent's *effective*
   * tier, which is why leaving the row alone keeps it inheriting: the panel only sends tiers that
   * differ from this value, so a later change to the parent still reaches it.
   */
  defaultTier: DifficultyTierName;
  isOverridden: boolean;
  /** See {@link AiGatewayRoute.inheritsFrom}. */
  inheritsFrom: string | null;
}

export interface AiDifficultyProfile {
  /**
   * `null` means the company has no profile of its own and is looking at the inherited platform
   * default. Saving writes a company-owned row that shadows it rather than editing it in place.
   */
  companyId: string | null;
  routingMode: RoutingModeName;
  /**
   * Whether per-question routing can be switched on at all. `false` means the deployment's
   * resilience/classifier kill switch is off, so the toggle stays disabled rather than bouncing.
   */
  dynamicRoutingAvailable: boolean;
  simpleAiModelId: string | null;
  simpleModelKey: string | null;
  moderateAiModelId: string | null;
  moderateModelKey: string | null;
  complexAiModelId: string | null;
  complexModelKey: string | null;
  classifierTier: DifficultyTierName;
  escalationEnabled: boolean;
  escalateMainTo: DifficultyTierName | null;
  isActive: boolean;
  routes: AiDifficultyRoute[];
}

export interface AiConnectorTestResult {
  success: boolean;
  connectorName: string;
  provider: string;
  model: string | null;
  apiDialect: ApiDialectName;
  durationMs: number;
  errorCode: AiGatewayErrorCode | null;
  errorMessage: string | null;
}

export interface CreateAiGatewayConnectorRequest {
  provider: string;
  name: string;
  /** Integer, not a name — see the note at the top of this file. */
  kind: number;
  apiDialect?: number | null;
  authScheme?: number | null;
  region?: string | null;
  catalogSyncMode: number;
  defaultModelName?: string | null;
  timeoutSeconds?: number | null;
  baseUrl?: string | null;
  apiKey?: string | null;
  isDefault: boolean;
  description?: string | null;
  isActive: boolean;
}

export interface UpdateAiGatewayConnectorRequest {
  name: string;
  kind: number;
  apiDialect?: number | null;
  authScheme?: number | null;
  region?: string | null;
  catalogSyncMode: number;
  defaultModelName?: string | null;
  timeoutSeconds?: number | null;
  baseUrl?: string | null;
  /** Omit to keep the stored key; send `""` to clear it and fall back to the platform key. */
  apiKey?: string | null;
  isDefault: boolean;
  description?: string | null;
  isActive: boolean;
}

export interface UpsertAiGatewayRouteRequest {
  /** Both ids `null` clears the binding — that is the reset path, there is no delete endpoint. */
  aiConnectorId: string | null;
  aiModelId: string | null;
  isActive: boolean;
}

export interface UpsertAiDifficultyProfileRequest {
  routingMode: number;
  /** `{ agentRoute: tierValue }`, tier as its integer. Empty object clears every override. */
  tierOverrides?: Record<string, number>;
  simpleAiModelId?: string | null;
  moderateAiModelId?: string | null;
  complexAiModelId?: string | null;
  classifierTier: number;
  escalationEnabled: boolean;
  escalateMainTo?: number | null;
  isActive: boolean;
}

/* ------------------------------------------------------------------------------------------- *
 * Cost attribution & budget alerts
 *
 * These DTOs are the one exception to the note at the top of this file: they expose their enums
 * raw, so `plane` and `period` are **integers in both directions** — `0`, not `"Tenant"`. Do not
 * add a `*Value` map for them and do not compare against a name.
 * ------------------------------------------------------------------------------------------- */

/** Which spend a report covers. `All` is every row, *not* Tenant + Platform as two series. */
export enum AiCostPlaneValue {
  Tenant = 0,
  Platform = 1,
  All = 2,
}

export enum AiBudgetPeriodValue {
  Monthly = 0,
  Weekly = 1,
  Daily = 2,
}

/**
 * One row of a cost breakdown.
 *
 * An empty `key`/`label` means the underlying column was null — an untagged call, or one that ran
 * outside any widget. Render a locally translated "unattributed" label for it; the server does not
 * send one because it has no locale.
 */
export interface AiCostBreakdownItem {
  key: string;
  label: string;
  costUsd: number;
  calls: number;
  /** Calls with no price row. Their tokens are counted, their cost is not. */
  unpricedCalls: number;
  inputTokens: number;
  outputTokens: number;
}

/** A single UTC day. The series is **sparse** — days with no traffic are absent, not zero. */
export interface AiCostDailyPoint {
  date: string;
  costUsd: number;
  calls: number;
  unpricedCalls: number;
  inputTokens: number;
  outputTokens: number;
}

export interface AiCostSummary {
  from: string;
  /** Exclusive. */
  to: string;
  /** The **clamped** window (1–90). Read the window off this, not off what was requested. */
  days: number;
  plane: AiCostPlaneValue;
  totalCostUsd: number;
  totalCalls: number;
  /**
   * Calls that produced no cost figure — pre-gateway rows with no token counts, and models with
   * no price row. There is no backfill, so historic calls stay here permanently.
   */
  unpricedCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  byModel: AiCostBreakdownItem[];
  byRoute: AiCostBreakdownItem[];
  byWidget: AiCostBreakdownItem[];
  byDay: AiCostDailyPoint[];
}

/* ------------------------------------------------------------------------------------------- *
 * Usage attribution (WP-70)
 *
 * Who spent the tokens and how: per customer company, customer, source (chat / workflow /
 * autonomous agent / portal), workflow, autonomous agent and job. Same window and plane rules as
 * the spend summary; the optional filters narrow every table and pick the allowance standing.
 * ------------------------------------------------------------------------------------------- */

/** Mirrors the API's `AiUsageSource`. */
export enum AiUsageSourceValue {
  Chat = 0,
  Workflow = 1,
  AutonomousAgent = 2,
  Portal = 3,
  Admin = 4,
}

export interface AiUsageBreakdownItem {
  /** `null` = the rows had no attribution (calls made before WP-70, or admin-app calls). */
  key: string | null;
  label: string | null;
  calls: number;
  unpricedCalls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface AiUsageJobItem {
  jobId: string;
  trigger: string;
  sourceKind: AiUsageSourceValue | null;
  sourceLabel: string | null;
  customerLabel: string | null;
  customerCompanyLabel: string | null;
  createdAt: string;
  status: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

/**
 * The standing of the tightest **enforced** budget in a scope. `enforced: false` means no hard
 * limit applies — the default; allowances are unlimited until a budget is enforced.
 */
export interface AiAllowanceStanding {
  enforced: boolean;
  budgetId: string | null;
  budgetName: string | null;
  amountUsd: number;
  spentUsd: number;
  remainingUsd: number;
  /** 0–100, capped once exhausted. */
  percentUsed: number;
  periodStart: string | null;
  periodEnd: string | null;
  exhausted: boolean;
}

export interface AiUsageSummary {
  from: string;
  to: string;
  /** 0 = the current calendar month; otherwise the clamped window (1–90). */
  days: number;
  plane: AiCostPlaneValue;
  totalCalls: number;
  unpricedCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  byCustomerCompany: AiUsageBreakdownItem[];
  byCustomer: AiUsageBreakdownItem[];
  bySource: AiUsageBreakdownItem[];
  byWorkflow: AiUsageBreakdownItem[];
  byAutonomousAgent: AiUsageBreakdownItem[];
  topJobs: AiUsageJobItem[];
  /** The filtered customer company's (or the workspace's) allowance standing. */
  allowance: AiAllowanceStanding | null;
}

/**
 * An explicit report window, as the Dashboard's shared date-range picker sends it (`startDate` /
 * `endDate`, ISO UTC). When given it replaces the `days` look-back; the server caps the span at 90
 * days back from the end and echoes the effective window on `from` / `to` / `days`.
 */
export interface AiReportRange {
  startDate: string;
  endDate: string;
}

const rangeParams = (range?: AiReportRange) =>
  range ? { startDate: range.startDate, endDate: range.endDate } : {};
export interface AiUsageFilter {
  customerCompanyId?: string | null;
  customerId?: string | null;
}

/**
 * A spend alert — and, with `enforceLimit`, a hard limit (WP-70). An alert-only budget never blocks
 * a chat turn; an enforced one refuses new work in its scope once its amount is used up and pauses
 * running jobs until the period rolls over or the amount changes. Measured against tenant-plane,
 * non-demo spend only.
 */
export interface AiBudget {
  id: string;
  name: string;
  amountUsd: number;
  period: AiBudgetPeriodValue;
  /** Percentages, ascending. Each fires at most once per period. */
  thresholds: number[];
  /** `null` = the whole workspace (unless `customerCompanyId` is set). */
  widgetId: string | null;
  /** `null` both for a workspace-wide budget and for a deleted widget — check `widgetId`. */
  widgetName: string | null;
  /** Scope to one customer company (WP-70); exclusive with `widgetId`. */
  customerCompanyId: string | null;
  customerCompanyName: string | null;
  /** Hard limit on; default false. */
  enforceLimit: boolean;
  isActive: boolean;
  /** Empty means the alert goes to every active workspace administrator, not to nobody. */
  notifyEmails: string[];
  periodStart: string;
  periodEnd: string;
  /** Evaluation is debounced, so this can move without an alert having fired yet. */
  currentSpendUsd: number;
  /** Can exceed 100 — nothing caps spend at the budget. */
  percentUsed: number;
  triggeredThresholds: number[];
  createdAt: string;
  updatedAt: string;
}

export interface AiBudgetAlert {
  id: string;
  aiBudgetId: string;
  budgetName: string;
  periodStart: string;
  thresholdPercent: number;
  triggeredAt: string;
  spendUsd: number;
  budgetAmountUsd: number;
}

export interface UpsertAiBudgetRequest {
  name: string;
  amountUsd: number;
  /** Integer, not a name. */
  period: number;
  /** Omit or send an empty list for the 50 / 80 / 100 default. */
  thresholds?: number[] | null;
  widgetId?: string | null;
  /** Scope to one customer company (WP-70); not together with `widgetId`. */
  customerCompanyId?: string | null;
  /** Enforce as a hard limit (WP-70). Default false. */
  enforceLimit?: boolean;
  isActive: boolean;
  /** Omit or send an empty list to fall back to the workspace administrators. Max 10. */
  notifyEmails?: string[] | null;
}

/* ------------------------------------------------------------------------------------------- *
 * Traceability
 *
 * Request and response payloads are stored whole, so what comes back here is the real call. A very
 * long payload is clipped for transport only — `payloadClipped` on the attempt says when that
 * happened, and `payloadTruncated` marks the rows that hold less than the whole call because they
 * were erased or written before payloads were stored in full.
 * ------------------------------------------------------------------------------------------- */

/**
 * One user turn — every LLM call made while answering a single message, collapsed into a row.
 *
 * `attemptCount` counts **physical provider calls**, so a turn that retried once reads as two.
 * The split between logical calls and their attempts is only in the detail view; the list would
 * need a `COUNT(DISTINCT …)` per row to show it, which is not worth it on the largest table a
 * tenant has.
 */
export interface AiTraceTurn {
  batchId: string;
  startedAt: string;
  endedAt: string;
  chatSessionId: string | null;
  widgetId: string | null;
  attemptCount: number;
  failedAttemptCount: number;
  /** Summed provider time, not wall-clock — parallel sub-agents overlap. */
  providerDurationMs: number;
  costUsd: number;
  /** Calls with no price row. Their tokens count, their cost does not. */
  unpricedCalls: number;
  inputTokens: number;
  outputTokens: number;
  routes: string[];
  models: string[];
}

export interface AiTraceTurnList {
  from: string;
  to: string;
  /** The **clamped** window (1–30). Read it off here, not off what was requested. */
  days: number;
  page: number;
  pageSize: number;
  totalTurns: number;
  failuresOnly: boolean;
  items: AiTraceTurn[];
}

/**
 * One physical call to a provider. A retry or a fallback is a new attempt on the same call, so
 * `attemptNumber` climbs while `traceId` stays put.
 */
export interface AiTraceAttempt {
  id: string;
  attemptNumber: number;
  /** Derived at read time — the highest attempt of its call, whether it succeeded or not. */
  isTerminal: boolean;
  executedAt: string;
  llmProvider: string;
  llmModel: string;
  connectorId: string | null;
  /** `route-binding` | `agent-config` | `call-override` | `platform-default`. */
  resolvedBy: string | null;
  isSuccess: boolean;
  httpStatusCode: number | null;
  errorMessage: string | null;
  durationMs: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  cachedInputTokens: number | null;
  reasoningTokens: number | null;
  estimatedCostUsd: number | null;
  priceVersion: string | null;
  responseId: string | null;
  /** The capture mode cut this payload down when it was written. */
  payloadTruncated: boolean;
  /** The payload was further clipped for transport — the stored row is longer than this. */
  payloadClipped: boolean;
  request: string | null;
  response: string | null;
}

/** One logical LLM call — a stage of the turn, with every attempt it took to complete. */
export interface AiTraceCall {
  traceId: string;
  /**
   * `false` for a row written before the trace columns existed, or by a caller outside the
   * gateway. Its attempts are still listed; they just cannot be ordered against each other.
   */
  hasTraceSpine: boolean;
  agentRoute: string | null;
  subAgentLabel: string | null;
  parentResponseId: string | null;
  responseId: string | null;
  startedAt: string;
  isSuccess: boolean;
  attemptCount: number;
  costUsd: number;
  /** Ordered by attempt number. Always at least one. */
  attempts: AiTraceAttempt[];
}

export interface AiTraceDetail {
  batchId: string;
  startedAt: string;
  endedAt: string;
  chatSessionId: string | null;
  widgetId: string | null;
  costUsd: number;
  unpricedCalls: number;
  inputTokens: number;
  outputTokens: number;
  calls: AiTraceCall[];
}

/**
 * One change to the gateway's configuration, recorded when it was made.
 *
 * `beforeJson` is null for a creation. Protected credentials are stripped from both snapshots
 * before they are stored, so a key is never readable from here.
 */
export interface GatewayConfigAuditEntry {
  id: string;
  /** `Connector` | `RouteBinding` | `ModelPolicy` | `DifficultyProfile` | `AiTracePayloads`. */
  entityType: string;
  entityId: string | null;
  entityName: string | null;
  /** `Created` | `Updated` | `Deleted`. */
  action: string;
  actorUserId: string | null;
  /** `null` when the change was made by a background process rather than a signed-in user. */
  actorEmail: string | null;
  createdAt: string;
  beforeJson: string | null;
  afterJson: string | null;
}

export const aiGatewayService = {
  getOverview: (): Promise<AiEffectiveConfig> =>
    apiClient.get("/ai-gateway/overview").then((r) => r.data),

  /** Already filtered server-side to models this company's policy permits. */
  getModels: (): Promise<AiModel[]> =>
    apiClient.get("/ai-gateway/models").then((r) => r.data),

  getConnectors: (): Promise<AiConnector[]> =>
    apiClient.get("/ai-gateway/connectors").then((r) => r.data),

  /** Circuit-breaker state. Only failing connectors are listed — see `AiConnectorHealth`. */
  getConnectorHealth: (): Promise<AiConnectorHealth[]> =>
    apiClient.get("/ai-gateway/connectors/health").then((r) => r.data),

  createConnector: (
    data: CreateAiGatewayConnectorRequest,
  ): Promise<AiConnector> =>
    apiClient
      .post("/ai-gateway/connectors", data, {
        __notifyOnSuccess: "app.aiGateway.notifications.connectorCreated",
      })
      .then((r) => r.data),

  updateConnector: (
    id: string,
    data: UpdateAiGatewayConnectorRequest,
  ): Promise<AiConnector> =>
    apiClient
      .put(`/ai-gateway/connectors/${id}`, data, {
        __notifyOnSuccess: "app.aiGateway.notifications.connectorSaved",
      })
      .then((r) => r.data),

  deleteConnector: (id: string): Promise<void> =>
    apiClient
      .delete(`/ai-gateway/connectors/${id}`, {
        __notifyOnSuccess: "app.aiGateway.notifications.connectorDeleted",
      })
      .then((r) => r.data),

  /**
   * One-token probe, never persisted. `__silent` because a failed probe comes back as a 200 with
   * `success: false` and belongs in the form, not in a toast — and a transport failure here is
   * still "the test did not pass", which the panel already renders.
   */
  testConnector: (id: string): Promise<AiConnectorTestResult> =>
    apiClient
      .post(`/ai-gateway/connectors/${id}/test`, undefined, { __silent: true })
      .then((r) => r.data),

  getRoutes: (): Promise<AiGatewayRoute[]> =>
    apiClient.get("/ai-gateway/routes").then((r) => r.data),

  upsertRoute: (
    agentRoute: string,
    data: UpsertAiGatewayRouteRequest,
  ): Promise<AiGatewayRoute> =>
    apiClient
      .put(`/ai-gateway/routes/${encodeURIComponent(agentRoute)}`, data, {
        __notifyOnSuccess: "app.aiGateway.notifications.routeSaved",
      })
      .then((r) => r.data),

  getDifficultyProfile: (): Promise<AiDifficultyProfile> =>
    apiClient.get("/ai-gateway/difficulty-profile").then((r) => r.data),

  upsertDifficultyProfile: (
    data: UpsertAiDifficultyProfileRequest,
  ): Promise<AiDifficultyProfile> =>
    apiClient
      .put("/ai-gateway/difficulty-profile", data, {
        __notifyOnSuccess: "app.aiGateway.notifications.difficultySaved",
      })
      .then((r) => r.data),

  /**
   * Spend for the last `days` days. `days` is clamped to 1–90 server-side rather than rejected,
   * and the clamped value comes back on `AiCostSummary.days`.
   */
  getCostSummary: (
    days: number,
    plane: AiCostPlaneValue,
    range?: AiReportRange,
  ): Promise<AiCostSummary> =>
    apiClient
      .get("/ai-gateway/costs/summary", {
        params: { days, plane, ...rangeParams(range) },
      })
      .then((r) => r.data),

  /**
   * Usage attribution (WP-70). `days = 0` means the current calendar month. The filters narrow every
   * table; `customerCompanyId` (or the customer's company) also picks which allowance standing comes back.
   */
  getUsage: (
    days: number,
    plane: AiCostPlaneValue,
    filter?: AiUsageFilter,
    range?: AiReportRange,
  ): Promise<AiUsageSummary> =>
    apiClient
      .get("/ai-gateway/costs/usage", {
        params: {
          days,
          plane,
          ...rangeParams(range),
          ...(filter?.customerCompanyId
            ? { customerCompanyId: filter.customerCompanyId }
            : {}),
          ...(filter?.customerId ? { customerId: filter.customerId } : {}),
        },
      })
      .then((r) => r.data),

  getBudgets: (): Promise<AiBudget[]> =>
    apiClient.get("/ai-gateway/budgets").then((r) => r.data),

  createBudget: (data: UpsertAiBudgetRequest): Promise<AiBudget> =>
    apiClient
      .post("/ai-gateway/budgets", data, {
        __notifyOnSuccess: "app.aiGateway.notifications.budgetCreated",
      })
      .then((r) => r.data),

  /** A full replace, not a patch — every field is written, including the ones left untouched. */
  updateBudget: (id: string, data: UpsertAiBudgetRequest): Promise<AiBudget> =>
    apiClient
      .put(`/ai-gateway/budgets/${id}`, data, {
        __notifyOnSuccess: "app.aiGateway.notifications.budgetSaved",
      })
      .then((r) => r.data),

  /** Takes the alert history with it. Deactivate instead if the history matters. */
  deleteBudget: (id: string): Promise<void> =>
    apiClient
      .delete(`/ai-gateway/budgets/${id}`, {
        __notifyOnSuccess: "app.aiGateway.notifications.budgetDeleted",
      })
      .then((r) => r.data),

  /** Newest first. `limit` is clamped to 1–200. */
  getBudgetAlerts: (limit: number): Promise<AiBudgetAlert[]> =>
    apiClient
      .get("/ai-gateway/budgets/alerts", { params: { limit } })
      .then((r) => r.data),

  /**
   * Turns, newest first. `days` is clamped to 1–30 and `pageSize` to 1–100, both echoed back on
   * the result. Platform-plane work is never included — it is StrikeUp's own overhead.
   */
  getTraces: (
    days: number,
    page: number,
    pageSize: number,
    failuresOnly: boolean,
  ): Promise<AiTraceTurnList> =>
    apiClient
      .get("/ai-gateway/traces", {
        params: { days, page, pageSize, failuresOnly },
      })
      .then((r) => r.data),

  /**
   * One turn in full. 404 both for a turn that never existed and for a platform-plane one — the
   * two are deliberately indistinguishable from here.
   */
  getTrace: (batchId: string): Promise<AiTraceDetail> =>
    apiClient.get(`/ai-gateway/traces/${batchId}`).then((r) => r.data),

  /** Gateway configuration changes, newest first. `limit` is clamped to 1–200. */
  getConfigChanges: (limit: number): Promise<GatewayConfigAuditEntry[]> =>
    apiClient
      .get("/ai-gateway/traces/config-changes", { params: { limit } })
      .then((r) => r.data),
};
